import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Camera, MapPin, ShieldCheck, X } from 'lucide-react'
import { btnPrimary } from '@/components/rm'
import { AnyPlacePicker } from '@/components/any-place-picker'
import { useT, type MessageKey } from '@/i18n'
import { useCategories } from '@/lib/listing'
import { photoProblem, usePostTask } from '@/lib/tasks'
import { todayIso } from '@/lib/today'
import type { AnyPlace } from '@/lib/search'
import { cn } from '@/lib/utils'

// Spec 005 US1. Post a task. Phone first.
export const Route = createFileRoute('/_client/tasks_/new')({
  validateSearch: (s: Record<string, unknown>): { category?: string } => ({ category: typeof s.category === 'string' ? s.category : undefined }),
  component: PostTask,
})

const PHOTO_MSG: Record<string, MessageKey> = { too_many: 'tasks.photoTooMany', too_big: 'tasks.photoTooBig', not_image: 'tasks.photoType' }
const field = 'h-11 w-full rounded-[10px] border border-token-subtle bg-token-surface px-3 text-[15px] text-token-primary outline-none focus:border-token-strong'

function PostTask() {
  const { t, lang } = useT()
  const navigate = useNavigate()
  const { category: initial } = Route.useSearch()
  const cats = useCategories()
  const post = usePostTask()
  const [category, setCategory] = useState(initial ?? '')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [place, setPlace] = useState<AnyPlace | null>(null)
  const [budget, setBudget] = useState('')
  const [date, setDate] = useState('')
  const [photos, setPhotos] = useState<File[]>([])
  const [error, setError] = useState('')

  const addPhotos = (list: FileList | null) => {
    const next = [...photos, ...Array.from(list ?? [])]
    const p = photoProblem(next)
    if (p) return setError(t(PHOTO_MSG[p]))
    setError(''); setPhotos(next)
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!category || title.trim().length < 3 || !place) return setError(t('tasks.missing'))
    setError('')
    try {
      const id = await post.mutateAsync({
        category_slug: category, title, description, place_code: place.code,
        budget_pesos: budget ? Number(budget) : null, preferred_date: date || null, photos,
      })
      navigate({ to: '/tasks/$id', params: { id } })
    } catch { setError(t('tasks.failed')) }
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <div>
        <h1 className="font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">{t('tasks.post')}</h1>
        <p className="mt-1 text-[13.5px] text-token-secondary">{t('tasks.postHint')}</p>
      </div>

      <section className="space-y-2">
        <p className="text-[13px] font-semibold text-token-secondary">{t('tasks.category')}</p>
        <div className="flex flex-wrap gap-2">
          {(cats.data ?? []).map((c) => (
            <button key={c.slug} type="button" aria-pressed={category === c.slug} onClick={() => setCategory(c.slug)}
              className={cn('rounded-full border px-3.5 py-1.5 text-[13px] font-semibold',
                category === c.slug ? 'border-brand bg-brand text-brand-fg' : 'border-token-subtle bg-token-surface text-token-secondary')}>
              {lang === 'fil' ? c.name_fil : c.name_en}
            </button>
          ))}
        </div>
      </section>

      <div className="space-y-1.5">
        <label htmlFor="ttitle" className="text-[13px] font-semibold text-token-secondary">{t('tasks.title')}</label>
        <input id="ttitle" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} className={field} placeholder={t('tasks.titlePlaceholder')} />
      </div>
      <div className="space-y-1.5">
        <label htmlFor="tdesc" className="text-[13px] font-semibold text-token-secondary">{t('tasks.description')}</label>
        <textarea id="tdesc" rows={4} value={description} maxLength={2000} onChange={(e) => setDescription(e.target.value)}
          className="w-full rounded-[10px] border border-token-subtle bg-token-surface px-3 py-2.5 text-[15px] text-token-primary outline-none focus:border-token-strong" placeholder={t('tasks.descriptionPlaceholder')} />
      </div>

      <div className="space-y-1.5">
        <p className="text-[13px] font-semibold text-token-secondary">{t('tasks.place')}</p>
        {place
          ? <div className="flex items-center justify-between gap-3 rounded-[12px] border border-token-subtle bg-token-surface px-3 py-2.5">
              <span className="flex min-w-0 items-center gap-2"><MapPin className="h-4 w-4 flex-none text-brand" />
                <span className="min-w-0"><span className="block truncate text-[15px] font-semibold text-token-primary">{place.name}</span>
                  {place.parent && <span className="block truncate text-[12px] text-token-secondary">{place.parent}</span>}</span></span>
              <button type="button" onClick={() => setPlace(null)} aria-label={t('search.change')}><X className="h-4 w-4 text-token-muted" /></button>
            </div>
          : <AnyPlacePicker placeholder={t('tasks.placePlaceholder')} onPick={(p) => {
              if (p.level === 'region' || p.level === 'province') return setError(t('tasks.placeLevel'))
              setError(''); setPlace(p)
            }} />}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label htmlFor="tbudget" className="text-[13px] font-semibold text-token-secondary">{t('tasks.budget')}</label>
          <input id="tbudget" type="number" inputMode="numeric" min={0} step={50} value={budget} onChange={(e) => setBudget(e.target.value)} className={field} placeholder="1500" />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="tdate" className="text-[13px] font-semibold text-token-secondary">{t('tasks.date')}</label>
          <input id="tdate" type="date" min={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} className={field} />
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-[13px] font-semibold text-token-secondary">{t('tasks.photos')}</p>
        <div className="flex flex-wrap gap-2">
          {photos.map((f, i) => (
            <span key={i} className="relative h-20 w-20 overflow-hidden rounded-[10px] border border-token-subtle">
              <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />
              <button type="button" aria-label="Remove" onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                className="absolute right-1 top-1 rounded-full bg-token-surface p-0.5"><X className="h-3.5 w-3.5" /></button>
            </span>
          ))}
          {photos.length < 5 && (
            <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-[10px] border border-dashed border-token-subtle text-[11px] font-semibold text-token-secondary">
              <Camera className="h-5 w-5" />{t('tasks.addPhotos')}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple className="sr-only" onChange={(e) => { addPhotos(e.target.files); e.target.value = '' }} />
            </label>
          )}
        </div>
      </div>

      <p className="flex items-start gap-2 text-[12.5px] text-token-secondary"><ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-brand" />{t('tasks.privacy')}</p>
      {error && <p role="alert" className="text-[13px] text-token-danger">{error}</p>}
      <button type="submit" disabled={post.isPending} className={`${btnPrimary} h-11 w-full justify-center`}>{post.isPending ? t('tasks.posting') : t('tasks.submit')}</button>
    </form>
  )
}
